import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, TextField } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material/';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import toast, { Toaster } from 'react-hot-toast';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';

import CashField from '../../AvslutteSkiftet/CashField';
import { formatSignedCash } from '../../../utils/formatCash';
import {
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../../theme/fieldTokens';
import { vendorTone, vendorPressSx } from '../../../theme/vendorTone';

// Flat tones for the counterpart tiles, warm for the till and cool for everything the drawer never sees
const CP_TONES = {
  till: '#8a4a00',
  outside: '#1f4f6f',
  minisafe: '#2f3a6e',
  bank: '#14543a',
};

// Where cash can come from and go, per location. Out of the safe only goes to the bank
const CP_OPTIONS = {
  miniIn: ['till', 'outside'],
  miniOut: ['till', 'outside'],
  safeIn: ['till', 'outside'],
  safeOut: ['bank'],
};

const CP_LABEL = {
  in: { till: 'safe.originTill', outside: 'safe.originOutside' },
  out: {
    till: 'safe.originTillOut',
    outside: 'safe.originOutsideOut',
    minisafe: 'safe.originMinisafe',
    bank: 'safe.originBank',
  },
};

const CP_HELP = {
  in: { till: 'safe.originTillHelp', outside: 'safe.originOutsideHelp' },
  out: {
    till: 'safe.originTillOutHelp',
    outside: 'safe.originOutsideOutHelp',
    minisafe: 'safe.originMinisafeHelp',
    bank: 'safe.originBankHelp',
  },
};

const panelSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  px: 3,
  py: 2,
  borderRadius: '14px',
  backgroundColor: '#151d3b',
  border: '1px solid #2a3a6e',
};

const amountSx = {
  fontSize: '38px',
  fontWeight: 700,
  color: '#e6eaf2',
  fontVariantNumeric: 'tabular-nums',
};

function ModalSafe() {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);

  const {
    hall,
    operator,
    session,
    updateShowModalSafe,
    updateIsLoadingScreen,
    updateFocusOnMainScreen,
  } = useContext(AppContext);

  const [miniSafe, setMiniSafe] = useState(null);
  const [safe, setSafe] = useState(null);

  // null → the two panels; otherwise the small form that action needs
  const [action, setAction] = useState(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // null until the operator says it out loud; no default, because assuming the till is what charged a drawer 116 000 kr it never held
  const [counterpart, setCounterpart] = useState(null);
  const isBusy = useRef(false);

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restored here rather than on each exit path, so no route out forgets
      updateFocusOnMainScreen(true);
    };
  }, []);

  const refresh = () => {
    socket.emit('miniSafeState', { bingoName: hall });
    socket.emit('safeState', { bingoName: hall });
  };

  useEffect(() => {
    if (hall) refresh();
  }, [hall]);

  useEffect(() => {
    const done = (label) => (data) => {
      updateIsLoadingScreen(false);
      isBusy.current = false;

      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      if (data.ok === false) {
        const key =
          {
            amountRequired: 'safe.amountRequired',
            reasonRequired: 'safe.reasonRequired',
            insufficientBalance: 'safe.insufficientBalance',
            counterpartRequired: 'safe.originRequired',
            originRequired: 'safe.originRequired',
          }[data.reason] ?? 'error.error';
        toast.error(t(key));
        return;
      }

      toast.success(t(label));
      setAction(null);
      setAmount('');
      setReason('');
      setCounterpart(null);
      refresh();
    };

    const handleMini = (data) => data && setMiniSafe(data);
    const handleSafe = (data) => data && setSafe(data);

    const handlers = {
      miniSafeState: handleMini,
      safeState: handleSafe,
      miniSafeMove: done('safe.moveDone'),
      miniSafeCount: done('safe.countDone'),
      safeMove: done('safe.moveDone'),
      safeCount: done('safe.countDone'),
    };

    Object.entries(handlers).forEach(([e, h]) => socket.on(e, h));
    return () => Object.entries(handlers).forEach(([e, h]) => socket.off(e, h));
  }, [socket, hall]);

  const emit = (event, payload) => {
    if (isBusy.current) return;
    isBusy.current = true;
    updateIsLoadingScreen(true);
    socket.emit(event, {
      bingoName: hall,
      operator: operator,
      session: session,
      ...payload,
    });
  };

  const submitAction = () => {
    if (amount === '' || Number(amount) < 0) {
      toast.error(t('safe.amountRequired'));
      return;
    }
    if (isCount) {
      emit(action === 'miniCount' ? 'miniSafeCount' : 'safeCount', {
        counted: Number(amount),
        reason,
      });
      return;
    }
    // A count moves nothing, so it is the one action with no counterpart to give
    if (counterpart === null) {
      toast.error(t('safe.originRequired'));
      return;
    }
    if (!reason.trim()) {
      toast.error(t('safe.reasonRequired'));
      return;
    }

    const direction = action.endsWith('In') ? 'in' : 'out';

    if (action.startsWith('mini')) {
      // The mini safe still speaks in a boolean; only the safe can send cash on
      emit('miniSafeMove', {
        direction,
        amount: Number(amount),
        reason,
        fromTill: counterpart === 'till',
      });
      return;
    }

    emit('safeMove', {
      direction,
      amount: Number(amount),
      reason,
      counterpart,
    });
  };

  const actionTile = (icon, label, onClick, tone) => (
    <button className={`btn-choice tone-${tone}`} onClick={onClick}>
      {icon}
      {label}
    </button>
  );

  const isCount = action === 'miniCount' || action === 'safeCount';
  const isOut = Boolean(action) && action.endsWith('Out');
  const options = CP_OPTIONS[action] ?? [];

  // Same idiom as the refusjon provider row: all lit until one is picked
  const counterpartTile = (key) => {
    const picked = counterpart === key;

    return (
      <Button
        key={key}
        variant="contained"
        onClick={() => setCounterpart(key)}
        sx={{
          height: '70px',
          width: options.length > 2 ? '190px' : '230px',
          minWidth: options.length > 2 ? '190px' : '230px',
          padding: 0,
          borderRadius: '12px',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '18px',
          textTransform: 'none',
          ...vendorTone(CP_TONES[key]),
          ...vendorPressSx,
          opacity: counterpart === null || picked ? 1 : 0.35,
          border: picked ? '3px solid #ffffff' : '3px solid transparent',
        }}
      >
        {t(CP_LABEL[isOut ? 'out' : 'in'][key])}
      </Button>
    );
  };

  const ACTION_TITLES = {
    miniIn: 'safe.miniSafeIn',
    miniOut: 'safe.miniSafeOut',
    miniCount: 'safe.miniSafeCount',
    safeIn: 'safe.safeIn',
    safeOut: 'safe.safeOut',
    safeCount: 'safe.safeCount',
  };

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div
            className="react-confirm-alert-body modal-status"
            style={{
              width: '96vw',
              maxWidth: '1500px',
              maxHeight: '94vh',
              overflowY: 'auto',
            }}
          >
            {/* Inside this overlay, not outside it. ModalCashMovement has its own
                for the same reason: this modal is rendered from context, in its
                own stacking context, so a Toaster anywhere else paints its
                toasts behind it however high the z-index goes */}
            <Toaster
              position="top-center"
              reverseOrder={false}
              toastOptions={{ style: { fontSize: '24px' } }}
              containerStyle={{ zIndex: 99999999 }}
            />

            <h1>{t(action ? ACTION_TITLES[action] : 'safe.title')}</h1>

            {action === null ? (
              <>
                <Box sx={{ display: 'flex', gap: 3, mt: 2, textAlign: 'left' }}>
                  {/* Mini safe */}
                  <Box sx={panelSx}>
                    <Typography
                      sx={{ color: '#8fa3d4', fontSize: '17px', mb: 0.5 }}
                    >
                      {t('safe.miniSafe')}
                    </Typography>
                    <Box sx={amountSx}>
                      {formatSignedCash(miniSafe?.balance ?? 0)}
                      <span style={{ fontSize: '20px', color: '#8fa3d4' }}>
                        {' '}
                        kr
                      </span>
                    </Box>

                    {/* Most days it does not move, so "counted" is the signal */}
                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '15px',
                        color: miniSafe?.countedToday ? '#5ad19b' : '#fcad89',
                      }}
                    >
                      {miniSafe?.countedToday
                        ? t('safe.countedToday')
                        : t('safe.notCountedToday')}
                    </Typography>

                    <Box className="choice-row" sx={{ mt: 'auto', pt: 2 }}>
                      {actionTile(
                        <AddCircleIcon />,
                        t('safe.miniSafeIn'),
                        () => setAction('miniIn'),
                        'in'
                      )}
                      {actionTile(
                        <RemoveCircleIcon />,
                        t('safe.miniSafeOut'),
                        () => setAction('miniOut'),
                        'out'
                      )}
                      {actionTile(
                        <FactCheckIcon />,
                        t('safe.miniSafeCount'),
                        () => setAction('miniCount'),
                        'safe'
                      )}
                    </Box>
                  </Box>

                  {/* Safe — same shape as the mini safe now that bags are gone */}
                  <Box sx={panelSx}>
                    <Typography
                      sx={{ color: '#8fa3d4', fontSize: '17px', mb: 0.5 }}
                    >
                      {t('safe.safe')}
                    </Typography>
                    <Box sx={amountSx}>
                      {formatSignedCash(safe?.balance ?? 0)}
                      <span style={{ fontSize: '20px', color: '#8fa3d4' }}>
                        {' '}
                        kr
                      </span>
                    </Box>

                    <Typography
                      sx={{
                        mt: 1,
                        fontSize: '15px',
                        color: safe?.countedToday ? '#5ad19b' : '#fcad89',
                      }}
                    >
                      {safe?.countedToday
                        ? t('safe.countedToday')
                        : t('safe.notCountedToday')}
                    </Typography>

                    <Box className="choice-row" sx={{ mt: 'auto', pt: 2 }}>
                      {actionTile(
                        <AddCircleIcon />,
                        t('safe.safeIn'),
                        () => setAction('safeIn'),
                        'in'
                      )}
                      {actionTile(
                        <RemoveCircleIcon />,
                        t('safe.safeOut'),
                        () => {
                          setAction('safeOut');
                          // A single destination leaves nothing to ask
                          setCounterpart('bank');
                        },
                        'out'
                      )}
                      {actionTile(
                        <FactCheckIcon />,
                        t('safe.safeCount'),
                        () => setAction('safeCount'),
                        'safe'
                      )}
                    </Box>
                  </Box>
                </Box>

                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '28px' }}
                >
                  <button
                    className="btn-custom-no"
                    onClick={() => updateShowModalSafe(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CancelIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.cancel')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Cap the two-field form's width so the comment box isn't a banner */}
                <Box sx={{ maxWidth: '620px', mx: 'auto', width: '100%' }}>
                  {isCount && (
                    <Typography
                      sx={{
                        color: '#c9d6f2',
                        fontSize: '20px',
                        mt: 2,
                        textAlign: 'center',
                      }}
                    >
                      {t('safe.countQuestion')}
                    </Typography>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: '20px',
                    }}
                  >
                    <CashField
                      label={
                        t(isCount ? 'safe.countedLabel' : 'safe.amount') + '*'
                      }
                      value={amount}
                      onChange={setAmount}
                      autoFocus
                    />
                  </div>

                  {/* Nothing preselected: the question the old screen never asked */}
                  {!isCount && (
                    <Box sx={{ mt: 3 }}>
                      <Typography
                        sx={{
                          color: '#c9d6f2',
                          fontSize: '19px',
                          mb: 1.5,
                          textAlign: 'center',
                        }}
                      >
                        {t(isOut ? 'safe.originTitleOut' : 'safe.originTitle')}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: '10px',
                          justifyContent: 'center',
                        }}
                      >
                        {options.map(counterpartTile)}
                      </Box>

                      <Typography sx={{ ...fieldHelpSx, minHeight: '22px' }}>
                        {counterpart === null
                          ? ''
                          : t(CP_HELP[isOut ? 'out' : 'in'][counterpart])}
                      </Typography>
                    </Box>
                  )}

                  <>
                    <TextField
                      autoComplete="off"
                      // No asterisk: only the server knows whether the count disagrees
                      label={
                        isCount
                          ? t('safe.countComment')
                          : t('safe.reason') + '*'
                      }
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      variant="outlined"
                      multiline
                      maxRows={4}
                      inputProps={{ maxLength: 500 }}
                      InputLabelProps={{ sx: verticallyCenteredLabelSx }}
                      className="modal-textfield"
                      style={{ width: '100%', marginTop: '24px' }}
                    />
                    <Typography sx={fieldHelpSx}>
                      {t(isCount ? 'safe.countCommentHelp' : 'safe.reasonHelp')}
                    </Typography>
                  </>
                </Box>

                <div className="react-confirm-alert-button-group">
                  <button
                    className="btn-custom-yes"
                    onClick={submitAction}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.confirm')}
                  </button>
                  <button
                    className="btn-custom-no"
                    onClick={() => {
                      setAction(null);
                      setAmount('');
                      setReason('');
                      setCounterpart(null);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.openShiftBack')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalSafe;
